import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Gift, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "fitly_lead_popup";
const FIRST_VISIT_DELAY = 15000;

export default function LeadCapturePopup() {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [step, setStep] = useState("form"); // form | submitting | success | error
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const exitListenerRef = useRef(false);

  const isAdmin = location.pathname.startsWith("/admin");

  const getState = useCallback(() => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    } catch { return {}; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- STORAGE_KEY is a module constant

  const setState = useCallback((patch) => {
    const current = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    const updated = { ...current, ...patch };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- STORAGE_KEY is a module constant

  // First visit: time-based trigger
  useEffect(() => {
    if (isAdmin) return;
    const state = getState();
    if (state.submitted || state.dismissed_at) {
      // Dismissed before — set up exit intent instead
      const dismissed = new Date(state.dismissed_at || 0);
      const hoursSince = (Date.now() - dismissed.getTime()) / 3600000;
      if (state.submitted || hoursSince < 24) return; // Don't show again within 24h if dismissed

      const handleExit = (e) => {
        if (e.clientY <= 5 && !exitListenerRef.current) {
          exitListenerRef.current = true;
          setShow(true);
        }
      };
      document.addEventListener("mouseout", handleExit);
      return () => document.removeEventListener("mouseout", handleExit);
    }

    // First visit — show after delay
    const timer = setTimeout(() => {
      if (!getState().submitted) setShow(true);
    }, FIRST_VISIT_DELAY);
    return () => clearTimeout(timer);
  }, [isAdmin, getState]);

  const handleDismiss = () => {
    setShow(false);
    setState({ dismissed_at: new Date().toISOString() });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStep("submitting");
    try {
      await axios.post(`${API}/leads/capture`, {
        ...formData,
        source_page: location.pathname,
      });
      setStep("success");
      setState({ submitted: true });
    } catch {
      setStep("error");
    }
  };

  if (isAdmin) return null;

  const inputClass = "w-full bg-[#0A1711] border border-[#F7F5F0]/10 px-4 py-3 text-[#F7F5F0] placeholder-[#A3B8AD]/40 focus:border-[#D4AF37]/50 focus:outline-none rounded-xl text-sm";

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="lead-capture-popup">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.35 }}
            className="relative bg-[#0D1F17] border border-[#F7F5F0]/10 w-full max-w-[420px] rounded-2xl overflow-hidden shadow-2xl"
          >
            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-[#A8D5BA] via-[#D4AF37] to-[#E8B4B8]" />

            <button onClick={handleDismiss} data-testid="lead-popup-dismiss"
              className="absolute top-4 right-4 p-1.5 text-[#A3B8AD]/50 hover:text-[#F7F5F0] transition-colors z-10 rounded-full hover:bg-[#F7F5F0]/5">
              <X size={18} />
            </button>

            <div className="px-7 pt-7 pb-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                  <Gift size={18} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Free Resource</p>
                  <h3 className="font-serif text-lg text-[#F7F5F0] leading-snug">Fertility Nutrition Guide</h3>
                </div>
              </div>
              <p className="text-sm text-[#A3B8AD] leading-relaxed">
                Get our 5-pillar guide to fertility nutrition — cycle-synced eating, biomarker tips, and quick wins you can start today. Sent straight to your inbox.
              </p>
            </div>

            <div className="px-7 pb-7 pt-4">
              {step === "success" ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="text-center py-4" data-testid="lead-popup-success">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#A8D5BA]/15 border border-[#A8D5BA]/25 flex items-center justify-center">
                    <Gift size={22} className="text-[#A8D5BA]" />
                  </div>
                  <p className="font-serif text-lg text-[#F7F5F0] mb-1">Guide sent!</p>
                  <p className="text-sm text-[#A3B8AD]">Check your inbox for the fertility nutrition guide.</p>
                  <button onClick={handleDismiss} data-testid="lead-popup-close-success"
                    className="mt-4 text-sm text-[#D4AF37] hover:underline">Continue browsing</button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="lead-capture-form">
                  <input type="text" required placeholder="Your name" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="lead-input-name" className={inputClass} />
                  <input type="email" required placeholder="your@email.com" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="lead-input-email" className={inputClass} />
                  <input type="tel" required placeholder="Phone number" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="lead-input-phone" className={inputClass} />
                  {step === "error" && (
                    <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
                  )}
                  <button type="submit" disabled={step === "submitting"} data-testid="lead-submit-btn"
                    className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0D1F17] py-3.5 text-sm font-bold tracking-wide rounded-full hover:bg-[#B5952F] transition-all disabled:opacity-50 shadow-lg shadow-[#D4AF37]/20 mt-1">
                    {step === "submitting" ? "Sending..." : <>Get Free Guide <ArrowRight size={14} /></>}
                  </button>
                  <p className="text-center text-[#A3B8AD]/30 text-[10px] mt-1">No spam. Unsubscribe anytime.</p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
